import {FeatureCollection} from '@turf/turf';
import {
  getCameraBoundingBox,
  getFontSizeRenderByCanvasRatio,
  toIGNBBoxString,
  updateDataSourceLabelSize
} from '../../cesiumTools';
import {LayerDatasource, LayerLoadType, MapperViewer} from '../../types';
import * as turf from '@turf/turf';

export class WatercourseLayer implements LayerDatasource {

  id = 'watercourse';
  name = 'Cours d\'eau';
  active = false;
  dataSource = new Cesium.GeoJsonDataSource(this.name);
  loadType = LayerLoadType.Manual;
  viewer: MapperViewer;

  url = 'https://data.geopf.fr/wfs?version=2.0.0&SERVICE=WFS&REQUEST=GetFeature&OUTPUTFORMAT=json&TYPENAME=BDTOPO_V3:troncon_hydrographique&COUNT=10000&BBOX=';
  geojson: FeatureCollection = null;

  hasLabel = true;
  labelVisible = true;
  labelDistance = 1500; // distance entre les labels en mètres

  previouslyLoadedBbox = null;
  isLoading = false;

  constructor() {}

  onBeforePrint(): void {
    updateDataSourceLabelSize(this.dataSource, this.viewer);
  }
  onAfterPrint(): void {
    updateDataSourceLabelSize(this.dataSource, this.viewer);
  }

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;
    viewer.dataSources.add(this.dataSource);
  }

  remove(): void {
    this.viewer.dataSources.remove(this.dataSource);
  }

  loadData(forceReload = false): void {
    if (!this.dataSource) {
      console.error('Viewer not initialized');
      return;
    }

    const bbox = toIGNBBoxString(getCameraBoundingBox(this.viewer));

    if (this.previouslyLoadedBbox === bbox && !forceReload) {
      return;
    }

    this.previouslyLoadedBbox = bbox;

    if (bbox) {
      this.isLoading = true;

      const url = `${this.url}${bbox}`;
      fetch(url)
        .then(res => res.json())
        .then((geojson: FeatureCollection) => {
          this.geojson = geojson;

          return this.buildWatercourse(geojson).then(() => {
            this.setLabel(this.labelDistance);
            this.isLoading = false;
          });
        }).catch(error => {
          console.error('[WatercourseLayer] -> (error) -> ', error);
          this.isLoading = false;
        });
    }
  }

  buildWatercourse(geojson: FeatureCollection): Promise<void> {
    return this.dataSource.load(geojson, {
      clampToGround: true,
      stroke: Cesium.Color.ROYALBLUE,
      strokeWidth: 4
    }).then(() => {
      this.dataSource.entities.values.forEach(entity => {
        if (entity.polyline) {
          // @ts-ignore
          entity.polyline.arcType = Cesium.ArcType.RHUMB;
        }
      });

      this.setActive(true);
    });
  }

  toggleLabels(): void {
    this.labelVisible = !this.labelVisible;
    this.dataSource.entities.values.forEach(entity => {
      if (entity.label) {
        entity.label.show = this.labelVisible;
      }
    });
  }

  clearLabel(): void {
    this.dataSource.entities.values.forEach(entity => {
      if (entity.label) {
        this.dataSource.entities.remove(entity);
      }
    });
  }

  setLabel(labelDistance): void {
    this.labelDistance = labelDistance;

    const watercourseMap = new Map();

    this.clearLabel();

    this.geojson?.features?.forEach(watercourse => {
      // On ne prend pas en compte la hauteur (z) des coordonnées
      if ('coordinates' in watercourse.geometry && watercourse.geometry.type === 'LineString') {
        watercourse.geometry.coordinates = watercourse.geometry.coordinates.map(a => a.slice(0, 2));

        const coordinates = watercourse.geometry.coordinates;
        const watercourseName = watercourse.properties.cpx_toponyme_de_cours_d_eau;

        if (!watercourseName) {
          return;
        }

        // regroupement des coordonnées (LineString) par nom de cours d'eau pour l'affichage des labels
        if (watercourseMap.has(watercourseName)) {
          watercourseMap.get(watercourseName).push(coordinates);
        } else {
          // On ne prend pas en compte les cours d'eau sans nom
          if (watercourseName) {
            watercourseMap.set(watercourseName, [coordinates]);
          }
        }
      }
    });

    const textStyle = getFontSizeRenderByCanvasRatio(this.viewer);

    watercourseMap.forEach((lineStringArray, watercourseName) => {

      const filteredLatLngList = lineStringArray
        .flat() // on prend les coordonnées de chaque LineString
        .reduce((acc, item) => { // on garde uniquement les points qui sont à plus de (x) mètres de distance
          if (acc.length === 0) {
            acc.push(item);
          } else if (acc.every((a) => turf.rhumbDistance(a, item, {units: 'meters'}) > labelDistance)) {
            acc.push(item);
          }
          return acc;
        }, []);

      // Render des labels
      filteredLatLngList.forEach((coord) => {
        this.dataSource.entities.add({
          position: Cesium.Cartesian3.fromDegrees(...coord),
          label: {
            text: watercourseName,
            disableDepthTestDistance: 200,
            fillColor: Cesium.Color.ROYALBLUE,
            showBackground: true,
            backgroundColor: Cesium.Color.WHITE,
            heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
            eyeOffset: new Cesium.Cartesian3(0, 0, -200),
            ...textStyle
          }
        });
      });
    });
  }

  setActive(active: boolean): void {
    this.active = active;
    this.dataSource.show = active;

    if (active) {
      this.loadData();
    }
  }
}

